<script lang="ts">
import { computed, defineComponent } from "vue";
import Zelle from "@/components/grid/Zelle.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import beschriftungUtils from "@/businesslogic/utils/beschriftungUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "AbschlussIhreDaten",
  emits: ["gehe-zu-antragsdaten"],
  components: {
    Zelle,
    TitelZelle,
    Zeile,
  },
  props: {
    produkt: String,
  },
  setup(props) {
    const piniaStore = useFormStore();

    const { anredeText } = beschriftungUtils();

    const formatTitelZelle = {
      breite: "3",
      class: "o-grid__cell--4",
      "ausrichtung-responsiv": false,
      "vertikal-zentrieren": false,
    };

    const formatZelle = {
      breite: "9",
      class: "o-grid__cell--8",
    };

    const getAnredeText = computed(() => {
      if (piniaStore.state.personendaten.versicherungsnehmer !== undefined) {
        return anredeText(piniaStore.state.personendaten.versicherungsnehmer.anrede);
      }
      return "";
    });

    const getPlzOrtText = computed(() => {
      let rueckgabe = "";
      if (piniaStore.state.personendaten.versicherungsnehmer.adresse !== undefined) {
        rueckgabe =
          piniaStore.state.personendaten.versicherungsnehmer.adresse.plz +
          " " +
          piniaStore.state.personendaten.versicherungsnehmer.adresse.ort;
      }
      if (
        props.produkt == "vgv" &&
        piniaStore.state.risikoAnschrift !== undefined &&
        !piniaStore.state.personendaten.versicherungsnehmer.adresseAbweichendVonRisikoadresse
      ) {
        rueckgabe = piniaStore.state.risikoAnschrift.plz + " " + piniaStore.state.risikoAnschrift.ort;
      }
      return rueckgabe;
    });

    const getStrasseHausnummerText = computed(() => {
      let rueckgabe = "";
      if (piniaStore.state.personendaten.versicherungsnehmer.adresse !== undefined) {
        rueckgabe =
          piniaStore.state.personendaten.versicherungsnehmer.adresse.strasse +
          " " +
          piniaStore.state.personendaten.versicherungsnehmer.adresse.hausnummer;
      }
      if (
        props.produkt == "vgv" &&
        piniaStore.state.risikoAnschrift !== undefined &&
        !piniaStore.state.personendaten.versicherungsnehmer.adresseAbweichendVonRisikoadresse
      ) {
        rueckgabe = piniaStore.state.risikoAnschrift.strasse + " " + piniaStore.state.risikoAnschrift.hausnummer;
      }
      return rueckgabe;
    });

    const getVerwalterText = computed(() => {
      let verwalterText = "";
      const verwalter = piniaStore.state.personendaten.verwalter;
      if (verwalter !== undefined) {
        verwalterText = anredeText(verwalter.anrede);

        if (verwalter.organisation && verwalter.anrede === "0003") {
          verwalterText += " " + verwalter.organisation + ", ";
        }
        verwalterText = (verwalterText + " " + verwalter.titel).trim();
        verwalterText += " " + (verwalter.vorname + " " + verwalter.nachname).trim() + ", ";
        verwalterText +=
          verwalter.adresse.strasse +
          " " +
          verwalter.adresse.hausnummer +
          ", " +
          verwalter.adresse.plz +
          " " +
          verwalter.adresse.ort +
          ", " +
          verwalter.email +
          ", Telefon: " +
          verwalter.telefon;
        if (verwalter.mobil) {
          verwalterText += ", Mobil: " + verwalter.mobil;
        }
      }
      return verwalterText;
    });

    const getUnterlagenDigitalText = computed(() => {
      if (piniaStore.state.personendaten.versicherungsnehmer.standardKommunikationsart === "EMAIL") {
        return "ja";
      }
      return "nein";
    });

    return {
      props,
      formatTitelZelle,
      formatZelle,
      piniaStore,
      getAnredeText,
      getPlzOrtText,
      getStrasseHausnummerText,
      getVerwalterText,
      getUnterlagenDigitalText,
    };
  },
});
</script>

<template>
  <div class="o-grid u-mar-t-2">
    <div class="o-grid__cell o-grid__cell--12 u-form-grid-nospace-y">
      <h2 class="u-form-heading">
        {{ piniaStore.state.isMakler === true ? "Kundendaten" : "Ihre Daten" }}&nbsp;
        <a
          role="button"
          tabindex="0"
          class="u-copy-s field-edit"
          @click="$emit('gehe-zu-antragsdaten', $event)"
          @keydown.enter="$emit('gehe-zu-antragsdaten', $event)"
          @keydown.space.prevent="$emit('gehe-zu-antragsdaten', $event)"
          aria-label="Ihre Daten bearbeiten"
          >bearbeiten</a
        >
      </h2>
    </div>
  </div>

  <zeile
    v-if="
      piniaStore.state.personendaten.versicherungsnehmer.anrede === '0001' ||
      piniaStore.state.personendaten.versicherungsnehmer.anrede === '0002'
    "
    class="u-mar-t-2"
  >
    <titel-zelle v-bind="formatTitelZelle">Anrede/Titel</titel-zelle>
    <zelle v-bind="formatZelle" data-cy="anredeTitel">
      <strong>
        {{ getAnredeText }}&nbsp;
        {{ piniaStore.state.personendaten.versicherungsnehmer.titel }}
      </strong>
    </zelle>
  </zeile>

  <zeile
    v-if="
      piniaStore.state.personendaten.versicherungsnehmer.anrede === '0003' ||
      piniaStore.state.personendaten.versicherungsnehmer.anrede === 'ETG' ||
      piniaStore.state.personendaten.versicherungsnehmer.anrede === 'WEG'
    "
    class="u-mar-t-2"
  >
    <titel-zelle v-bind="formatTitelZelle">
      <span v-html="getAnredeText"></span>
    </titel-zelle>
    <zelle v-bind="formatZelle" data-cy="organisation">
      <strong>
        {{ piniaStore.state.personendaten.versicherungsnehmer.organisation }}
      </strong>
    </zelle>
  </zeile>

  <zeile
    v-if="
      piniaStore.state.personendaten.versicherungsnehmer.anrede === '0001' ||
      piniaStore.state.personendaten.versicherungsnehmer.anrede === '0002' ||
      piniaStore.state.personendaten.versicherungsnehmer.anrede === '0003' ||
      piniaStore.state.personendaten.versicherungsnehmer.anrede === 'Z001'
    "
    class="u-mar-t-2"
  >
    <titel-zelle v-bind="formatTitelZelle">Vorname/Name</titel-zelle>
    <zelle v-bind="formatZelle" data-cy="vornameNachname">
      <strong>
        {{ piniaStore.state.personendaten.versicherungsnehmer.vorname }}&nbsp;
        {{ piniaStore.state.personendaten.versicherungsnehmer.nachname }}
      </strong>
    </zelle>
  </zeile>

  <div v-if="!piniaStore.state.personendaten.verwalterAktiv">
    <zeile class="u-mar-t-2">
      <titel-zelle v-bind="formatTitelZelle"> Post&shy;leit&shy;zahl/&shy;Ort</titel-zelle>
      <zelle v-bind="formatZelle" data-cy="plzOrt">
        <strong>
          {{ getPlzOrtText }}
        </strong>
      </zelle>
    </zeile>

    <zeile class="u-mar-t-2">
      <titel-zelle v-bind="formatTitelZelle"> Straße/Haus&shy;nummer</titel-zelle>
      <zelle v-bind="formatZelle" data-cy="strasseHausnummer">
        <strong>
          {{ getStrasseHausnummerText }}
        </strong>
      </zelle>
    </zeile>
  </div>

  <zeile v-if="piniaStore.state.personendaten.versicherungsnehmer.email" class="u-mar-t-2">
    <titel-zelle v-bind="formatTitelZelle">E-Mail</titel-zelle>
    <zelle v-bind="formatZelle" data-cy="email">
      <strong>{{ piniaStore.state.personendaten.versicherungsnehmer.email }}</strong>
    </zelle>
  </zeile>

  <zeile v-if="piniaStore.state.personendaten.versicherungsnehmer.telefon" class="u-mar-t-2">
    <titel-zelle v-bind="formatTitelZelle">Telefon</titel-zelle>
    <zelle v-bind="formatZelle" data-cy="telefon">
      <strong>
        {{ piniaStore.state.personendaten.versicherungsnehmer.telefon }}
      </strong>
    </zelle>
  </zeile>

  <div>
    <zeile class="u-mar-t-2">
      <titel-zelle v-bind="formatTitelZelle"> Ver&shy;trags&shy;unter&shy;lagen per E-Mail</titel-zelle>
      <zelle data-cy="unterlagenDigital" v-bind="formatZelle">
        <strong>
          {{ getUnterlagenDigitalText }}
        </strong>
      </zelle>
    </zeile>
  </div>

  <zeile v-if="piniaStore.state.personendaten.versicherungsnehmer.mobil" class="u-mar-t-2">
    <titel-zelle v-bind="formatTitelZelle">Mobil&shy;telefon</titel-zelle>
    <zelle v-bind="formatZelle" data-cy="mobil">
      <strong>
        {{ piniaStore.state.personendaten.versicherungsnehmer.mobil }}
      </strong>
    </zelle>
  </zeile>

  <zeile v-if="piniaStore.state.personendaten.verwalterAktiv" class="u-mar-t-2">
    <titel-zelle v-bind="formatTitelZelle"> Ansprech&shy;part&shy;ner/Ver&shy;wal&shy;ter</titel-zelle>
    <zelle v-bind="formatZelle" data-cy="plzOrt">
      <strong>
        {{ getVerwalterText }}
      </strong>
    </zelle>
  </zeile>
</template>
